<template>
  <template></template>
</template>

<script setup lang="ts">
import { reactive, onMounted, onBeforeUnmount } from 'vue';
import { map, filter, groupBy, debounceTime, mergeMap, fromEvent, Subscription } from 'rxjs';

import { SseListener } from '@/utils/general';
import getEnv, { isLocalEnv, isProdEnv } from '@/utils/env';
import { EnvironmentTypes } from '@/types/environmentTypes';
import { useEventStreamStore } from '@/store/eventStreamStore';

interface State {
  registered: boolean;
  dispatcher?: EventSource;
  subscriptions: Array<Subscription>;
}
const state: State = reactive({
  registered: false,
  dispatcher: undefined,
  subscriptions: []
});

const isProd = isProdEnv();

onMounted(() => {
  !isProd && console.debug('YapSse: onMounted -> registering EventSource and RxJS subscriptions...');
  registerEventSourceObservers();
  window.addEventListener('online', () => {
    !isProd && console.debug('YapSSE: we are back online --> registering EventSource and RxJS subscriptions...');
    registerEventSourceObservers();
  });
  window.addEventListener('offline', () => {
    !isProd &&
      console.debug('YapSSE: We are now offline -> unsubscribing from streams & disconnecting from eventSource...');
    unregisterEventSourceObservers();
  });
});

onBeforeUnmount(() => {
  !isProd &&
    console.debug('YapSse: onBeforeUnmount -> unregistering EventSource and unsubscribing RxJS subscriptions...');
  unregisterEventSourceObservers();
});

const listeners = [
  SseListener.DebitCard,
  SseListener.CardProgram,
  SseListener.CardProgramContract,
  SseListener.CardInCardProgram,
  SseListener.AgentInPartnership,
  SseListener.ExportTicket,
  SseListener.CardBulkOrder,
  SseListener.CashSubAccount,
  SseListener.CashAccountCreated,
  SseListener.EntitlementChanged,
  SseListener.Beneficiary,
  SseListener.PaymentOrder,
  SseListener.TrxReceipt
];

const eventStreamStore = useEventStreamStore();

function registerEventSourceObservers() {
  if (state.dispatcher) return;

  const dispatcherUrl = getEnv(EnvironmentTypes.VITE_APP_DISPATCHER_URL);
  state.dispatcher = new EventSource(dispatcherUrl, { withCredentials: true });

  if (state.dispatcher) {
    const isLocal = isLocalEnv();
    listeners.forEach((listener) => {
      if (!state.dispatcher) return;
      const subscription = fromEvent(state.dispatcher, listener)
        .pipe(
          filter((ev: any) => {
            return dispatcherUrl.startsWith(ev.origin) || isLocal;
          }),
          map((ev: any) => {
            return {
              type: listener,
              time: ev.timeStamp,
              message: JSON.parse(ev.data)
            };
          }),
          map((entry) => {
            !isProd && console.debug(`RxJS: listener [${listener}] got`, entry);
            let guid;
            switch (listener) {
              case SseListener.DebitCard:
                guid = entry.message.cardUid;
                break;
              case SseListener.CardProgram:
              case SseListener.CardInCardProgram:
              case SseListener.CardProgramContract:
                guid = entry.message.cardProgramUid;
                break;
              case SseListener.ExportTicket:
                guid = entry.message.exportTicketUID;
                break;
              case SseListener.CardBulkOrder:
                guid = entry.message.orderUid;
                break;
              case SseListener.CashSubAccount:
                guid = entry.message.cashAccountUid;
                break;
              case SseListener.CashAccountCreated:
                guid = entry.message.rootEntityUid;
                break;
              case SseListener.EntitlementChanged:
                guid = entry.message.rootEntityUid;
                break;
              case SseListener.Beneficiary:
                guid = entry.message.beneficiaryUID;
                break;
              case SseListener.PaymentOrder:
                guid = entry.message.paymentOrderUid;
                break;
              case SseListener.TrxReceipt:
                guid = entry.message.trxUid ?? entry.message.paymentOrderUid;
                break;
              case SseListener.AgentInPartnership:
                guid = entry.message.partnershipUid;
                break;
              default:
                guid = entry.message.uid;
                break;
            }
            return { ...entry, guid };
          }),
          groupBy((entry) => {
            !isProd && console.debug(`RxJS: listener [${listener}] grouping`, entry.guid);
            return entry.guid;
          }),
          mergeMap((group$) => {
            !isProd && console.debug(`RxJS: listener [${listener}] debouncing`, group$);
            return group$.pipe(debounceTime(700));
          })
        )
        .subscribe({
          next: (entry) => {
            !isProd && console.debug(`RxJS: listener [${listener}] handling`, entry);
            eventStreamStore.handleEventSourceMessage(entry);
          },
          error: (error) => console.error(`RxJS: listener [${listener}] error`, error),
          complete: () => !isProd && console.debug(`RxJS: listener [${listener}] completed.`)
        });
      state.subscriptions.push(subscription);
    });
  }
}

function unregisterEventSourceObservers() {
  !isProd && console.debug('SSE: unregisterEventSourceObservers');
  unsubscribeStreams();
  state.dispatcher?.close();
  state.dispatcher = undefined;
}

function unsubscribeStreams() {
  !isProd && console.debug('Unsubscribing from all RxJS subscriptions...');
  state.subscriptions.forEach((subscription) => {
    subscription.unsubscribe();
    !isProd && console.debug('   unsubscribed from', subscription);
  });
  state.subscriptions = [];
}
</script>
